<template>
	<component
		:is="computedElement"
		:to="isLinkTarget ? to : undefined"
		:data-disabled="disabled"
		:data-tone="tone"
		data-component="button"
		:aria-label="label"
		@click="handleClick"
		@mouseenter="handleMouseEnter"
		@mouseleave="handleMouseLeave"
	>
		<div ref="el" class="inner" :class="[size, { 'icon-only': isIconOnly }]">
			<span class="bg" />
			<span v-if="icon && iconPosition === 'back'" class="icon-container">
				<div class="icon-inner">
					<Icon :slug="icon" class="icon" size="md" />
				</div>
			</span>
			<span v-if="label" class="label-container">
				<span class="-body-regular label">{{ label }}</span>
			</span>
			<span v-if="icon && iconPosition === 'front'" class="icon-container">
				<div class="icon-inner">
					<Icon :slug="icon" class="icon" size="md" />
				</div>
			</span>
		</div>
	</component>
</template>

<script setup>
import { HyperLink, Icon } from '~/components/core'
import { useEmitter } from '~/composables/core'

const props = defineProps({
	icon: { type: [Boolean, String], default: false },
	tone: { type: String, default: 'primary', validator: (v) => ['primary', 'secondary'].includes(v) },
	iconPosition: { type: String, default: 'front', validator: (v) => ['front', 'back'].includes(v) },
	size: { type: [Boolean, String], default: 'medium' },
	label: { type: [Boolean, String], default: false },
	to: { type: [Boolean, Object], default: false },
	actionType: { type: [Boolean, String], default: false },
	hover: { type: Boolean, default: false },
	disabled: { type: Boolean, default: false },
	clickFunc: { type: Function, default: () => {} },
	as: { type: [Boolean, String], default: false },
})

const el = ref(null)
const emitter = useEmitter()

const resolvedActionType = computed(() => props.actionType || props.to?.actionType || false)
const isTypeformAction = computed(() => resolvedActionType.value === 'typeform')
const isLinkTarget = computed(() => !!props.to && props.to.type !== 'action')
const isIconOnly = computed(() => !!props.icon && !props.label)

const computedElement = computed(() => (props.as ? props.as : isLinkTarget.value ? HyperLink : 'button'))
const handleClick = () => {
	if (props.disabled) return

	props.clickFunc()

	if (!isLinkTarget.value && isTypeformAction.value) {
		emitter.emit('typeform-open')
	}
}

const handleMouseEnter = () => {}
const handleMouseLeave = () => {}

const setup = () => {}
onMounted(() => setup())

defineExpose({
	click: () => el.value.$el.click(),
})
</script>

<style lang="scss" scoped>
[data-component='button'] {
	position: relative;

	@include extend_hitbox(5);

	.inner {
		position: relative;

		display: inline-flex;
		align-items: center;
		justify-content: center;
		gap: rs(8, 8);
		height: rs(52, 52);

		padding: 0 rs(18, 14);

		// overflow: hidden;

		&.large {
			height: rs(52, 52);
		}
		&.medium {
			height: rs(46, 46);

			.label-container {
				.label {
					@include body-small-regular;
				}
			}
		}

		&.small {
			height: rs(44, 40);
		}

		&.icon-only {
			padding: 0;
			aspect-ratio: 1 / 1;
		}
	}

	@media (hover: hover) {
		&:hover {
			.bg {
				opacity: 0.7;
				transform: scale(1.04);
			}
		}
	}

	&[data-tone='primary'] {
		--icon-color: var(--color-geral-white, var(--color-geral-white));

		.label {
			color: var(--color-geral-white, var(--color-geral-white));
		}

		.bg {
			background: var(--color-river-blue-500, var(--color-geral-white));
		}
	}

	&[data-tone='secondary'] {
		--icon-color: var(--color-river-blue-500, var(--color-geral-white));

		.label {
			color: var(--color-river-blue-500, var(--color-geral-white));
		}

		.bg {
			background: var(--color-river-blue-100, var(--color-geral-white));
		}
		@media (hover: hover) {
			&:hover {
				.bg {
					opacity: 0.75;
				}
			}
		}
	}

	.bg {
		position: absolute;
		inset: 0;

		border-radius: rs(10, 12);

		transition: 0.5s ease(out-quint);
	}

	.icon-container {
		position: relative;

		display: grid;
		align-items: center;
		justify-content: center;

		overflow: hidden;

		.icon-inner {
			position: relative;

			display: flex;
			align-items: center;
			justify-content: center;
			width: rs(16, 16);
			height: rs(16, 16);

			grid-row: 1;
			grid-column: 1;

			overflow: hidden;
		}
		.icon {
			position: relative;

			grid-row: 1;
			grid-column: 1;
		}
	}

	.label-container {
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.label {
		position: relative;
		white-space: nowrap;
	}
}
</style>
